import React, { useEffect, useState } from 'react';
import { moodboardService } from '../lib/moodboardService';
import { RoomCategory, RoomType } from '../types/moodboard';

const RoomTypeManager: React.FC = () => {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [categories, setCategories] = useState<RoomCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newRoomTypeName, setNewRoomTypeName] = useState('');
  const [newRoomTypeDescription, setNewRoomTypeDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [editingRoomType, setEditingRoomType] = useState<RoomType | null>(null);

  const fetchRoomTypes = async () => {
    try {
      const roomTypeList = await moodboardService.getAllRoomTypes();
      setRoomTypes(roomTypeList);
    } catch (err) {
      // In a real app, you might want to handle this error more gracefully
      console.error(err);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const categoryList = await moodboardService.getRoomCategories();
      setCategories(categoryList);
      if (categoryList.length > 0) {
        setSelectedCategory(categoryList[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchRoomTypes();
  }, []);

  const handleAddRoomType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoomTypeName.trim() || !selectedCategory) {
      return;
    }

    try {
      await moodboardService.addRoomType(newRoomTypeName, selectedCategory, newRoomTypeDescription);
      setNewRoomTypeName('');
      setNewRoomTypeDescription('');
      fetchRoomTypes(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleUpdateRoomType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRoomType) return;

    try {
      await moodboardService.updateRoomType(editingRoomType.id, editingRoomType.name, editingRoomType.description || '', editingRoomType.category_id);
      setEditingRoomType(null);
      fetchRoomTypes(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  if (loading) return <p>Loading room types...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="bg-white shadow rounded-lg p-4 mt-6">
      <h3 className="text-lg font-semibold mb-2">Room Types</h3>
      <form onSubmit={handleAddRoomType} className="space-y-2 mb-4">
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={newRoomTypeName}
          onChange={(e) => setNewRoomTypeName(e.target.value)}
          placeholder="New room type name"
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
        <input
          type="text"
          value={newRoomTypeDescription}
          onChange={(e) => setNewRoomTypeDescription(e.target.value)}
          placeholder="Description"
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-.md"
        />
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          Add
        </button>
      </form>
      <ul className="divide-y divide-gray-200">
        {roomTypes.map((roomType) => (
          <li key={roomType.id} className="py-2">
            {editingRoomType && editingRoomType.id === roomType.id ? (
              <form onSubmit={handleUpdateRoomType} className="space-y-2">
                <select
                  value={editingRoomType.category_id}
                  onChange={(e) => setEditingRoomType({ ...editingRoomType, category_id: e.target.value })}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={editingRoomType.name}
                  onChange={(e) => setEditingRoomType({ ...editingRoomType, name: e.target.value })}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
                <input
                  type="text"
                  value={editingRoomType.description || ''}
                  onChange={(e) => setEditingRoomType({ ...editingRoomType, description: e.target.value })}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
                <div className="flex space-x-2">
                  <button type="submit" className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700">Save</button>
                  <button type="button" onClick={() => setEditingRoomType(null)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            ) : (
              <div className="flex justify-between items-center">
                <div>
                  <strong>{roomType.name}:</strong> {roomType.description}
                </div>
                <button onClick={() => setEditingRoomType(roomType)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">Edit</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RoomTypeManager; 